import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { StatusBar, useGlobalCopyStore, useScreenSize } from '../../common'
import { formatCurrency } from '../../core/utils/NumberFormatting'
interface RecurringStatusProps {
  expected: number
  isExpense?: boolean
  missed?: number
  paid: number
  title: string
  total: number
  upcoming: number
}

export const RecurringStatus = observer(
  ({ expected, isExpense = true, missed, paid, title, total, upcoming }: RecurringStatusProps) => {
    const theme = useTheme()
    const { isSmallMobile } = useScreenSize()
    const { recurring: recurringCopy } = useGlobalCopyStore()

    const data = React.useMemo(() => {
      const recurrence = [{ color: theme.palette.success.main, percentage: `${paid / expected}` }]
      if (missed) {
        recurrence.push({ color: theme.palette.error.main, percentage: `${missed / expected}` })
      }
      return recurrence
    }, [paid, expected, missed])

    return (
      <Stack>
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', pb: 12 }}>
          <Text bold={true} variant="body1">
            {`${title} (${total})`}
          </Text>
          <Text bold={true} color={isExpense ? 'text.primary' : 'success.main'} variant="body1">
            {`${isExpense ? '-' : '+'}${formatCurrency(expected, '0,0.00')}`}
          </Text>
        </Stack>

        <StatusBar data={data} height={12} />

        <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 2, my: 8 }}>
          <Box sx={{ bgcolor: 'success.main', height: 8, mr: 8, width: 8 }} />
          {!isSmallMobile && (
            <Text bold={true} variant="body2">
              {formatCurrency(paid, '0,0.00')}
            </Text>
          )}
          <Text variant="caption">{recurringCopy.paid?.toLowerCase()}</Text>

          {missed !== undefined && (
            <>
              <Box sx={{ bgcolor: 'error.main', height: 8, ml: 16, mr: 8, width: 8 }} />
              {!isSmallMobile && (
                <Text bold={true} variant="body2">
                  {formatCurrency(missed, '0,0.00')}
                </Text>
              )}
              <Text variant="caption">{recurringCopy.missed?.toLowerCase()}</Text>
            </>
          )}

          <Box sx={{ bgcolor: 'divider', height: 8, ml: 16, mr: 8, width: 8 }} />
          {!isSmallMobile && (
            <Text bold={true} variant="body2">
              {formatCurrency(upcoming, '0,0.00')}
            </Text>
          )}
          <Text variant="caption">{recurringCopy.upcoming?.toLowerCase()}</Text>
        </Stack>
      </Stack>
    )
  },
)
